// Halaman render DIORAMA (kota Jawsel sebagai diorama kertas 3D, Three.js) — dipakai headless oleh backend/animatic/render.py.
// /diorama?naskah=<id> membaca /animatic/naskah/<id>.timeline.json dan menyediakan window.__animatic.tick(t) (frame deterministik).
// Naskah: kamera3d per scene (keyframe pos/lihat atau ikut "penerjun"), penerjun (keluar pesawat → mendarat), pesawat, awan, elemen overlay 2D.
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { BokehPass } from "three/examples/jsm/postprocessing/BokehPass.js";
import { api } from "../lib/api";
import { emptyState } from "../lib/engine";
import { ease } from "../animatic/jamVirtual";
import { buatOverlay, gambarPesawat, gambarParasut } from "../animatic/overlay";
import { gambarPakGub } from "../animatic/pakGub";
import { buatDunia, buatAwan, buatAktor, gridKeDunia } from "../diorama/dunia";

const W = 1080, H = 1920;
const v3 = (a, alt) => (Array.isArray(a) ? gridKeDunia(a[0], a[1], a[2] ?? alt ?? 0) : a.clone());
const lerpV = (a, b, p) => a.clone().lerp(b, p);

// Lintasan penerjun (waktu absolut): di pesawat sampai `keluar`, jatuh cepat lalu melayang (kanopi) sampai `mendarat`, lalu berdiri di tanah.
function posPenerjun(P, t, posPesawat) {
  const ke = v3(P.ke, 0);
  if (t < P.keluar) return { pos: posPesawat(P.keluar).clone().add(new THREE.Vector3(0, -40, 0)), fase: "pesawat", buka: 0 };
  if (t >= P.mendarat) return { pos: ke, fase: "darat", buka: Math.max(0, 1 - (t - P.mendarat) / 0.7) };
  const p = (t - P.keluar) / (P.mendarat - P.keluar), awal = posPesawat(P.keluar);
  const alt = awal.y * Math.pow(1 - p, P.kurva ?? 1.7);
  const u = ease.out(p);
  const pos = new THREE.Vector3(awal.x + (ke.x - awal.x) * u + Math.sin(t * 1.7) * 26, alt, awal.z + (ke.z - awal.z) * u);
  return { pos, fase: "terjun", buka: ease.back(ease.clamp((t - P.keluar - (P.bebas ?? 0.9)) / 0.5)) };
}

export default function Diorama() {
  const glRef = useRef(null), ovKanvasRef = useRef(null), ovRef = useRef(null);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("naskah") || "terjun_uji";
    let aktif = true, renderer = null;
    (async () => {
      window.__animatic = { tahap: "muat" };
      const [tl, cat] = await Promise.all([fetch(`/animatic/naskah/${id}.timeline.json?${Date.now()}`).then(r => r.json()), api.catalog()]);
      const defs = cat.buildings || [];
      const state = (await api.publicCity(tl.kota || "kediri")).state || emptyState();
      if (!aktif) return;
      window.__animatic.tahap = "dunia";
      const { scene } = buatDunia(state, defs, { malam: tl.langit === "night" ? 1 : 0 });
      const pusat = gridKeDunia(30, 30);
      if (tl.awan !== false) scene.add(buatAwan(tl.awan?.n ?? 14, 3, pusat, tl.awan?.altMin ?? 900, tl.awan?.altMax ?? 1800));

      // aktor: pesawat (lintasan lurus), penerjun (kanopi) & Pak Gub berdiri setelah mendarat
      const PS = tl.pesawat, posPesawat = t => { if (!PS) return pusat.clone().setY(2400); const p = ease.clamp((t - (PS.mulai ?? 0)) / (PS.durasi ?? 8)); return lerpV(v3(PS.dari), v3(PS.ke), p); };
      const pesawat = buatAktor(300, 170, 150, 90, PS?.skala ?? 1.6); scene.add(pesawat.sprite);
      const penerjun = buatAktor(300, 440, 150, 280, tl.penerjun?.skala ?? 0.6); scene.add(penerjun.sprite);
      const gub = buatAktor(260, 420, 130, 400, 0.3); scene.add(gub.sprite);

      renderer = new THREE.WebGLRenderer({ canvas: glRef.current, antialias: true, alpha: false, preserveDrawingBuffer: true });
      // resolusi internal WebGL (GPU perangkat lunak headless lambat): default 0.75 → 810x1440, kanvas ditampilkan 1080x1920 (overlay DOM tetap tajam)
      const skala = parseFloat(new URLSearchParams(window.location.search).get("skala") || tl.skalaRender || 0.75);
      renderer.setPixelRatio(1); renderer.setSize(Math.round(W * skala), Math.round(H * skala), false); renderer.setClearColor(0x3a80be, 1);
      const kamera = new THREE.PerspectiveCamera(55, W / H, 5, 30000);
      const composer = new EffectComposer(renderer);
      composer.setSize(Math.round(W * skala), Math.round(H * skala));
      composer.addPass(new RenderPass(scene, kamera));
      const bokeh = new BokehPass(scene, kamera, { focus: 800, aperture: 0.00012, maxblur: 0.012 });
      bokeh.enabled = new URLSearchParams(window.location.search).get("bokeh") !== "0";
      composer.addPass(bokeh);
      const ov = buatOverlay(ovRef.current, ovKanvasRef.current);

      // kamera: keyframe {t, pos:[gx,gy,alt] | ikut:"penerjun"+offset:[dx,dalt,dz], lihat:[gx,gy,alt] | "penerjun"(+lihatOffset), fov, bokeh}
      function nilaiKey(k, t, pj) {
        const acuan = k.ikut === "pesawat" || k.lihat === "pesawat" ? posPesawat(t) : pj.pos; // kamera menempel pada pesawat atau penerjun
        const pos = k.ikut ? acuan.clone().add(new THREE.Vector3(...(k.offset || [0, 300, 500]))) : v3(k.pos, 1500);
        const lihat = k.lihat === "penerjun" || k.lihat === "pesawat" ? acuan.clone().add(new THREE.Vector3(...(k.lihatOffset || [0, 0, 0]))) : v3(k.lihat || [30, 30, 0], 0);
        return { pos, lihat, fov: k.fov ?? 55, bokeh: k.bokeh ?? 0.00012 };
      }
      function aturKamera(sc, tLokal, t, pj) {
        const ks = sc.kamera3d || [{ t: 0, pos: [30, 30, 2500], lihat: [30, 30, 0] }];
        let a = ks[0], b = ks[0];
        for (let i = 0; i < ks.length; i++) { if (ks[i].t <= tLokal) { a = ks[i]; b = ks[i + 1] || ks[i]; } }
        const p = b === a ? 0 : (a.lancar === false ? ease.clamp : ease.inOut)(ease.clamp((tLokal - a.t) / (b.t - a.t)));
        const A = nilaiKey(a, t, pj), B = nilaiKey(b, t, pj);
        const pos = lerpV(A.pos, B.pos, p), lihat = lerpV(A.lihat, B.lihat, p);
        let dx = 0, dy = 0; // goyang kamera (hentakan mendarat dsb.)
        for (const g of sc.goyang || []) { const u = tLokal - g.pada, d = g.durasi ?? 0.5; if (u >= 0 && u < d) { const k = (1 - u / d) * (g.kuat ?? 20); dx += Math.sin(u * 71) * k; dy += Math.cos(u * 97) * k; } }
        kamera.position.copy(pos); kamera.up.set(0, 0, -1); kamera.lookAt(lihat);
        kamera.rotateZ((sc.miring ?? 0) * Math.PI / 180);
        kamera.position.add(new THREE.Vector3(dx, 0, dy));
        kamera.fov = A.fov + (B.fov - A.fov) * p; kamera.updateProjectionMatrix();
        bokeh.uniforms.focus.value = pos.distanceTo(lihat); bokeh.uniforms.aperture.value = A.bokeh + (B.bokeh - A.bokeh) * p;
      }

      function tick(t) {
        const sc = tl.scenes.find(s => t >= s.mulai && t < s.mulai + s.durasi) || tl.scenes[tl.scenes.length - 1];
        const tLokal = t - sc.mulai;
        const pj = tl.penerjun ? posPenerjun(tl.penerjun, t, posPesawat) : { pos: pusat.clone().setY(600), fase: "terjun", buka: 1 };
        // pesawat
        if (PS) { pesawat.sprite.visible = t >= (PS.mulai ?? 0) - 0.1 && t <= (PS.mulai ?? 0) + (PS.durasi ?? 8) + 0.1; pesawat.sprite.position.copy(posPesawat(t)); pesawat.gambar(g => { if (PS.arah === "kiri") g.scale(-1, 1); gambarPesawat(g, t); }); }
        else pesawat.sprite.visible = false;
        // penerjun & Pak Gub
        penerjun.sprite.visible = pj.fase === "terjun" || (pj.fase === "darat" && pj.buka > 0.02);
        penerjun.sprite.position.copy(pj.pos); if (pj.fase === "darat") penerjun.sprite.position.y += 40;
        if (penerjun.sprite.visible) penerjun.gambar(g => { g.rotate(Math.sin(t * 1.7) * 0.12 * (pj.fase === "terjun" ? 1 : 0)); gambarParasut(g, t, Math.max(0.02, pj.buka)); });
        gub.sprite.visible = pj.fase === "darat";
        gub.sprite.position.copy(pj.pos).add(new THREE.Vector3(0, 2, 0));
        if (gub.sprite.visible) gub.gambar(g => { g.scale(0.4, 0.4); gambarPakGub(g, t, { pose: t - tl.penerjun.mendarat < 1.2 ? "lambai" : "idle", ekspresi: "datar" }); });
        aturKamera(sc, tLokal, t, pj);
        composer.render();
        ov.gambar(sc, tLokal, W, H, []);
      }
      window.__animatic.tahap = "praputar";
      tick(0);
      window.__animatic = { tick, total: tl.total, fps: tl.fps || 30, ready: true, jpeg: q => glRef.current.toDataURL("image/jpeg", q ?? 0.92) };
    })().catch(e => { window.__animatic = { error: String(e && e.stack || e) }; });
    return () => { aktif = false; renderer?.dispose(); };
  }, []);
  return (
    <div data-testid="diorama-root" style={{ position: "relative", width: W, height: H, overflow: "hidden", background: "#3a80be" }}>
      <canvas ref={glRef} width={W} height={H} style={{ position: "absolute", left: 0, top: 0, width: W, height: H }} />
      <canvas ref={ovKanvasRef} width={W} height={H} style={{ position: "absolute", left: 0, top: 0, width: W, height: H }} />
      <div ref={ovRef} style={{ position: "absolute", left: 0, top: 0, width: W, height: H }} />
    </div>
  );
}
